import React, {useState, useEffect, useCallback, useRef} from 'react';
import {View, Text, TextInput, Pressable} from 'react-native';
import moment, {Moment} from 'moment';
import Icon from '../Shared/Icon';
import {DynamicTimePickerProps} from '../../Types/ControlTypes';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import Colors from '../../Styles/Shared/Colors';
import {Formats} from '../../Constants/Formats';
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';

const ALLOWED_INPUT_FORMATS = [
  Formats.FRONTEND_TIME,
  'H:mm',
  'h:mm A',
  'h:mmA',
];

const DynamicTimePicker = (
  props: DynamicTimePickerProps,
): React.ReactElement => {
  const {config = {}} = props;
  const [date, setDate] = useState<Date | null>(getValueFromProps(props.value));
  const [inputValue, setInputValue] = useState<string>(getFormattedValue(date));
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const onChange = useCallback((val: Moment | string) => {
      if (moment.isMoment(val) && val.isValid()) {
          let timeoutTime = 700;
          if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
          }

          timeoutRef.current = setTimeout(() => {
              const newDate = val.toDate();
              setDate(newDate);
              setInputValue(getFormattedValue(newDate));
              validateInput(newDate);
          }, timeoutTime);
      }
  }, []);

  useEffect(() => {
    const newDate = getValueFromProps(props.value);
    setDate(newDate);
    setInputValue(getFormattedValue(newDate));
  }, [props.value]);

  useEffect(() => {
    const backendVal = date ? moment(date).format(Formats.BACKEND_TIME) : '';
    const isValid = !validateInput(date);

    let metadataKey: string | null = null;
    if (props.config?.hasMetaData && props.config?.metaDataKey)
      metadataKey = props.config.metaDataKey;

    props.onChange?.(
      props.controlId,
      props.controlTypeId,
      backendVal,
      isValid,
      metadataKey,
      backendVal,
    );
  }, [date]);

  useEffect(() => {
    if (props.showError) {
      validateInput(date);
    }
  }, [props.showError]);

  function getFormattedValue(d: Date | null): string {
    return d ? moment(d).format(Formats.FRONTEND_TIME) : '';
  }

  function getValueFromProps(val?: string): Date | null {
    if (!val) return null;
    const allFormats = [Formats.BACKEND_TIME, ...ALLOWED_INPUT_FORMATS];
    const m = moment(val, allFormats, true);
    return m.isValid() ? m.toDate() : null;
  }

  function validateInput(d: Date | null): string {
    let msg = '';
    if (config.required && !d) {
      msg = `"${props.label}" is required`;
    }
    setError(msg);
    return msg;
  }

  const renderInput = (
    dtProps: any,
    openCalendar: Function,
  ): React.ReactElement => {
    const activateAndOpen = () => {
      if (!date) {
        const now = moment().toDate();
        setDate(now);
        setInputValue(getFormattedValue(now));
      }

      setTimeout(() => {
        openCalendar();
      }, 0);
    };

    const handleFocus = () => {
      setIsFocused(true);
      activateAndOpen();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const txt = e.target.value;
      setInputValue(txt);
      if (moment(txt, ALLOWED_INPUT_FORMATS, true).isValid()) {
        setError('');
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (isCalendarOpen){return;}
      setIsFocused(false);
      dtProps.onBlur?.(e);
      const m = moment(inputValue, ALLOWED_INPUT_FORMATS, true);
      const finalDate = m.isValid() ? m.toDate() : date;
      setDate(finalDate);
      setInputValue(getFormattedValue(finalDate));
      validateInput(finalDate);
    };

    return (
      <View style={{position: 'relative'}}>
        <TextInput
          {...dtProps}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={config.placeholder || 'Click or type to set time'}
          style={{...ControlsStyleSheet.input, outline: 'none'}}
        />
        <Pressable
          style={ControlsStyleSheet.iconContainer}
          onPress={activateAndOpen}>
          <Icon
            icon="clock"
            color={
              error ? Colors.red : isFocused ? Colors.green : Colors.darkGreen
            }
            size={21}
          />
        </Pressable>
      </View>
    );
  };

  return (
    <View
      style={{
        zIndex: isFocused ? 10 : 1,
        display: props.visible === false ? 'none' : 'flex',
      }}>
      <Text style={ControlsStyleSheet.label}>
        {props.label}
        <Text style={ControlsStyleSheet.required}>
          {config.required ? '*' : ''}
        </Text>
      </Text>
      <Datetime
        dateFormat={false}
        timeFormat={Formats.FRONTEND_TIME}
        value={date || undefined}
        onChange={onChange}
        renderInput={renderInput}
        timeConstraints={
          config.timeConstraints ?? {minutes: {min: 0, max: 59, step: 1}}
        }
        closeOnSelect
        onOpen={() => setIsCalendarOpen(true)}
        onClose={() => setIsCalendarOpen(false)}
      />
      <input
        type="time"
        style={{position: 'absolute', width: 0, height: 0, opacity: 0}}
        value={date ? moment(date).format(Formats.BACKEND_TIME) : ''}
        readOnly
        data-testid={`${props.controlId}-hidden-input`}
      />

      <Text style={ControlsStyleSheet.error}>{error}</Text>
    </View>
  );
};

export default DynamicTimePicker;
